/**
 * Admin → Recruiter Teams — typed api-client calls + TanStack Query hooks
 * (E05-F05-T04).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). The transport uses
 * the shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) and `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` so the SPA speaks the exact same
 * shapes as the Fastify API.
 *
 * Endpoints (1:1 with the legacy n8n `manage_teams` action router — get_teams /
 * create_team / update_team / delete_team, docs/source-n8n/reference/Teams.md +
 * dashboard.js loadTeams/saveTeam/deleteTeam):
 *   GET    /admin/teams       → useTeams()        (get_teams)
 *   POST   /admin/teams       → useCreateTeam()   (create_team)
 *   PATCH  /admin/teams/:id   → useUpdateTeam()   (update_team)
 *   DELETE /admin/teams/:id   → useDeleteTeam()   (delete_team, HARD)
 *
 * The legacy SPA POSTed `{ command:'manage_teams', action, ... }` to one webhook;
 * the v2 transport is clean REST but the `command` tag is still passed for the
 * legacy-success unwrap to mirror the other admin tabs.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  teamsListResponseSchema,
  teamCreateResponseSchema,
  teamUpdateResponseSchema,
  teamDeleteResponseSchema,
  type TeamDTO,
  type CreateTeamInput,
  type UpdateTeamInput,
  type TeamCreateResponse,
  type TeamUpdateResponse,
  type TeamDeleteResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const TEAMS_QUERY_KEY = ['admin', 'teams'] as const;

/** The legacy command tag carried on every request (manage_teams router). */
const TEAMS_COMMAND = { command: 'manage_teams' } as const;

/** Fetch all active recruiter teams (server orders by name; members derived). */
async function fetchTeams(): Promise<TeamDTO[]> {
  const result = await apiClient.get<unknown>('/admin/teams', TEAMS_COMMAND);
  const body = teamsListResponseSchema.parse(unwrap(result));
  return body.teams;
}

/** Create a recruiter team (name required; teamlead_id optional). */
async function postTeam(input: CreateTeamInput): Promise<TeamCreateResponse> {
  const result = await apiClient.post<unknown>('/admin/teams', input, TEAMS_COMMAND);
  return teamCreateResponseSchema.parse(unwrap(result));
}

/** Full-replace a team's name + teamlead_id by id. */
async function patchTeam(args: {
  id: number;
  patch: UpdateTeamInput;
}): Promise<TeamUpdateResponse> {
  const result = await apiClient.patch<unknown>(
    `/admin/teams/${encodeURIComponent(String(args.id))}`,
    args.patch,
    TEAMS_COMMAND,
  );
  return teamUpdateResponseSchema.parse(unwrap(result));
}

/** HARD-delete a recruiter team by id (legacy parity: always answers success). */
async function deleteTeamReq(id: number): Promise<TeamDeleteResponse> {
  const result = await apiClient.delete<unknown>(
    `/admin/teams/${encodeURIComponent(String(id))}`,
    TEAMS_COMMAND,
  );
  return teamDeleteResponseSchema.parse(unwrap(result));
}

/** List query — the recruiter-teams cards. */
export function useTeams(): UseQueryResult<TeamDTO[], ApiResultError> {
  return useQuery<TeamDTO[], ApiResultError>({
    queryKey: TEAMS_QUERY_KEY,
    queryFn: fetchTeams,
  });
}

/** Create mutation — invalidates the list so the new team appears. */
export function useCreateTeam(): UseMutationResult<
  TeamCreateResponse,
  ApiResultError,
  CreateTeamInput
> {
  const queryClient = useQueryClient();
  return useMutation<TeamCreateResponse, ApiResultError, CreateTeamInput>({
    mutationFn: postTeam,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY });
    },
  });
}

/** Update mutation — invalidates the list so the edit re-renders. */
export function useUpdateTeam(): UseMutationResult<
  TeamUpdateResponse,
  ApiResultError,
  { id: number; patch: UpdateTeamInput }
> {
  const queryClient = useQueryClient();
  return useMutation<TeamUpdateResponse, ApiResultError, { id: number; patch: UpdateTeamInput }>({
    mutationFn: patchTeam,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY });
    },
  });
}

/** Delete mutation (HARD) — invalidates the list so the removed team disappears. */
export function useDeleteTeam(): UseMutationResult<TeamDeleteResponse, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<TeamDeleteResponse, ApiResultError, number>({
    mutationFn: deleteTeamReq,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY });
    },
  });
}
